import { AddressId } from '../domain/entities/Address';
import { diContainer, serviceNames } from '../diContainer/diContainer';
import ShippingRepository from '../repositories/ShippingRepository';
import BillingRepository from '../repositories/BillingRepository';
import Config from '../domain/entities/Config';
import TransactionLoadHandler, { ITransactionLoad } from './TransactionLoadHandler';
import { FIELD_TYPE } from '../constants/Constants';

class EditShippingAddressUseCase extends TransactionLoadHandler {
  private id: AddressId;

  private shippingRepository: ShippingRepository;

  private billingRepository: BillingRepository;

  private config: Config;

  private isBillingSameAsShipping: boolean;

  constructor(id: AddressId, isBillingSameAsShipping: boolean, options?: ITransactionLoad) {
    super({ ...options?.transactionLoadOptions, isControlled: true });
    this.id = id;
    this.isBillingSameAsShipping = isBillingSameAsShipping;
    this.transactionRepository = diContainer.get(serviceNames.transactionRepository);
    this.shippingRepository = diContainer.get(serviceNames.shippingRepository);
    this.billingRepository = diContainer.get(serviceNames.billingRepository);
    this.config = diContainer.get(serviceNames.config);
  }

  public async run(): Promise<void> {
    const transaction = await this.transactionRepository.getTransaction();
    const shippingAddress = this.shippingRepository.getAddressById(this.id);
    if (this.id !== transaction.shippingAddress.id) {
      // reload shipping methods if new selected country is different from previous saved transaction countryId
      const shouldLoadShippingMethods =
            shippingAddress?.attributes?.[FIELD_TYPE.COUNTRY_ID] !== transaction?.shippingAddress?.attributes?.[FIELD_TYPE.COUNTRY_ID];
      if (shouldLoadShippingMethods) {
        await this.transactionRepository.loadShippingMethods(shippingAddress?.attributes?.[FIELD_TYPE.COUNTRY_ID], {
          debounceRequest: false,
          reloadTransaction: false,
        });
      }
      await this.shippingRepository.onSelectAddress(this.id);
      // we are required to set the address on form before updating the transaction
      // as we have race condition issues with the AddressForm initialize - fix it within RCO-9164 Spike
      this.shippingRepository.formStore.setAddress(shippingAddress);
      transaction.setShippingAddress(shippingAddress);
      if (this.isBillingSameAsShipping && !this.config.skipBillingAddress) {
        this.billingRepository.selectAddress(transaction.shippingAddress.id);
      }
    } else {
      this.shippingRepository.formStore.setAddress(shippingAddress);
    }
    this.shippingRepository.formStore.setShowErrors(true);

    await this.shippingRepository.updateAddressSchemaByCountry(shippingAddress.attributes[FIELD_TYPE.COUNTRY_ID]);
    this.shippingRepository.setEditingAddress(true);

    if (this.shippingRepository.config?.useShippingCalculator || transaction.isDirty) {
      await this.handleTransactionUpdate();
    }
  }
}

export default EditShippingAddressUseCase;
